import { useState, useEffect, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import dayjs from 'dayjs'
import { map, forEach } from 'lodash'
import useNodeStatisticsResource from '../../../shared/resources/api/v1/admin/useNodeStatisticsResource'
import StatisticsAreaChart from '../../../shared/components/charts/StatisticsAreaChart'
import formatBytes from '../../../shared/utils/format/formatBytes'
import { TIME_RANGES } from '../../../shared/constants/statisticsConfig'

const UNITS = [
  { value: 'percentage', labelId: 'nodes.statistics.unit.percentage' },
  { value: 'bytes', labelId: 'nodes.statistics.unit.bytes' },
]

const METRICS = ['cpu', 'memory', 'disk']

function NodeStatisticsSection({ teamSlug, nodeId }) {
  const { getStatistics } = useNodeStatisticsResource(teamSlug, nodeId)
  const [timeRange, setTimeRange] = useState('1h')
  const [unit, setUnit] = useState('percentage')
  const [statistics, setStatistics] = useState({
    cpu: { data: [], loading: true },
    memory: { data: [], loading: true },
    disk: { data: [], loading: true },
  })
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const fetchStatistics = useCallback(() => {
    const selectedRange = TIME_RANGES.find((r) => r.value === timeRange)
    const after = dayjs().subtract(selectedRange.hours, 'hour').toISOString()
    const params = {
      interval: selectedRange.interval,
      after,
      limit: selectedRange.limit,
    }

    setStatistics((prev) => ({
      cpu: { ...prev.cpu, loading: true },
      memory: { ...prev.memory, loading: true },
      disk: { ...prev.disk, loading: true },
    }))

    forEach(METRICS, (metric) => {
      getStatistics(metric, params)
        .then((response) => {
          if (!mountedRef.current) return
          setStatistics((prev) => ({
            ...prev,
            [metric]: { data: response.statistics || [], loading: false },
          }))
        })
        .catch(() => {
          if (!mountedRef.current) return
          setStatistics((prev) => ({
            ...prev,
            [metric]: { data: [], loading: false },
          }))
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange])

  useEffect(() => {
    fetchStatistics()
  }, [fetchStatistics])

  const handleTimeRangeChange = useCallback((event) => {
    setTimeRange(event.target.value)
  }, [])

  const handleUnitChange = useCallback((event) => {
    setUnit(event.target.value)
  }, [])

  const getValueFormatter = useCallback((metric) => {
    if (metric === 'cpu') {
      return (value) => value != null ? `${value.toFixed(1)}%` : ''
    }

    if (unit === 'percentage') {
      return (value) => value != null ? `${value.toFixed(1)}%` : ''
    }

    return (value) => formatBytes(value)
  }, [unit])

  const getChartData = useCallback((metric) => {
    const data = statistics[metric].data

    if (metric === 'cpu' || unit === 'percentage') {
      return data
    }

    return map(data, (item) => ({
      bucket: item.bucket,
      avg: item.avgBytes,
      min: item.minBytes,
      max: item.maxBytes,
    }))
  }, [statistics, unit])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          <FormattedMessage id="nodes.statistics.title" />
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            size="small"
            value={timeRange}
            onChange={handleTimeRangeChange}
            label={<FormattedMessage id="nodes.statistics.timeRangeLabel" />}
            sx={{ minWidth: 120 }}
          >
            {map(TIME_RANGES, (range) => (
              <MenuItem key={range.value} value={range.value}>
                <FormattedMessage id={range.labelId} />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={unit}
            onChange={handleUnitChange}
            label={<FormattedMessage id="nodes.statistics.unitLabel" />}
            sx={{ minWidth: 120 }}
          >
            {map(UNITS, (u) => (
              <MenuItem key={u.value} value={u.value}>
                <FormattedMessage id={u.labelId} />
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <StatisticsAreaChart
        titleId="nodes.statistics.cpu"
        data={getChartData('cpu')}
        loading={statistics.cpu.loading}
        formatValue={getValueFormatter('cpu')}
      />

      <StatisticsAreaChart
        titleId="nodes.statistics.memory"
        data={getChartData('memory')}
        loading={statistics.memory.loading}
        formatValue={getValueFormatter('memory')}
      />

      <StatisticsAreaChart
        titleId="nodes.statistics.disk"
        data={getChartData('disk')}
        loading={statistics.disk.loading}
        formatValue={getValueFormatter('disk')}
      />
    </Box>
  )
}

export default NodeStatisticsSection
